import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { TabItem } from "widgets";

import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.ts";

import { simpleFieldValidation } from "../../../app-utils/form.js";
import {
  type BamSummary,
  IndexStatus,
  type ProjectFile,
  type TrackNgsConfiguration,
  type TrackNgsCoverageConfiguration,
  type TrackNgsOrderedBamFilesData,
} from "../../../graphql.js";
import { isBamFile, isIndexRequiredForGenomeViewer } from "../../../modules/files/utils/file.js";
import { type IFileState } from "../../../redux/modules/files.js";
import {
  type ISortableTrack,
  loadBamFileSummary,
  setGenomeBrowserNgsTrackCustomization,
} from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import {
  DEFAULT_COVERAGE_HISTOGRAM_COLOR,
  NGS_READS_PILEUP_COLORING,
  NGS_READS_PILEUP_DENSITY,
  TRACK_TYPE,
} from "../../../settings/settings.js";
import {
  default as RadioButtonSelector,
  type IRadioProps,
  RadioButtonReduxFormField,
} from "../../core/forms/RadioButtonSelector.js";
import { TabDialog } from "../../core/overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import { DEFAULT_NGS_LANES_COUNT } from "./_mod.js";
import { type FileSelection, TrackFilesSelection } from "./TrackFilesSelection.js";

export interface INgsTrackCustomizationDialogOwnProps {
  onClose: () => void;
}

interface INgsTrackCustomizationDialogAppStateProps {
  selectedYAxisScaleType: CoverageScaleType;
  initialValues: Partial<FormData>;
  initiallySelectedBAMFiles: TrackNgsOrderedBamFilesData[];
  indexingStatusLookup: IFileState["indexingStatusLookup"];
}

interface INgsTrackCustomizationDialogDispatchProps {
  applyNgsConfig: (config: Partial<TrackNgsConfiguration>) => void;
  getProjectFileById: (fileId: number) => Promise<BamSummary>;
}

export interface INgsTrackCustomizationDialogProps
  extends INgsTrackCustomizationDialogOwnProps,
    INgsTrackCustomizationDialogAppStateProps,
    INgsTrackCustomizationDialogDispatchProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(state: IAppState): INgsTrackCustomizationDialogAppStateProps {
  const track: ISortableTrack = state.genomeBrowser.tracks.find((tr: ISortableTrack) => tr.type === TRACK_TYPE.NGS);
  const ngsConfiguration: TrackNgsConfiguration = track?.trackCustomization?.ngsConfiguration;
  const coverageConfig: TrackNgsCoverageConfiguration = ngsConfiguration?.coverageConfig;
  return {
    selectedYAxisScaleType: formValueSelector(NgsTrackCustomizationDialog.FORM_NAME)(
      state,
      NgsTrackCustomizationDialog.FIELD_SCALE_TYPE,
    ),
    initialValues: {
      [NgsTrackCustomizationDialog.FIELD_SCALE_TYPE]:
        coverageConfig?.yAxisScaling || DEFAULT_NGS_COVERAGE_CONFIGURATION.yAxisScaling,
      [NgsTrackCustomizationDialog.FIELD_SCALE_LINLOG]: coverageConfig?.logScale
        ? CoverageScaleLinLog.LOG
        : CoverageScaleLinLog.LIN,
      [NgsTrackCustomizationDialog.FIELD_SCALE_MIN]:
        coverageConfig?.yAxisScalingMin || DEFAULT_NGS_COVERAGE_CONFIGURATION.yAxisScalingMin,
      [NgsTrackCustomizationDialog.FIELD_SCALE_MAX]:
        coverageConfig?.yAxisScalingMax || DEFAULT_NGS_COVERAGE_CONFIGURATION.yAxisScalingMax,
    },
    initiallySelectedBAMFiles: ngsConfiguration?.orderedBamFiles || [],
    indexingStatusLookup: state.files.indexingStatusLookup,
  };
}

function mapDispatchToProps(dispatch): INgsTrackCustomizationDialogDispatchProps {
  return {
    applyNgsConfig: (config: Partial<TrackNgsConfiguration>) => dispatch(setGenomeBrowserNgsTrackCustomization(config)),
    getProjectFileById: (fileId: number) => dispatch(loadBamFileSummary(fileId)),
  };
}

export interface INgsTrackCustomizationDialogState {
  selectedBAMFiles: FileSelection[];
}

class NgsTrackCustomizationDialog extends PureComponent<
  INgsTrackCustomizationDialogProps,
  INgsTrackCustomizationDialogState
> {
  public static FORM_NAME = "ngsTrackCustomization";
  public static FIELD_SCALE_TYPE = "yaxis-scale-type";
  public static FIELD_SCALE_MIN = "yaxis-scale-min";
  public static FIELD_SCALE_MAX = "yaxis-scale-max";
  public static FIELD_SCALE_LINLOG = "yaxis-scale-linlog";
  public static FIELD_HIDE_SOFTCLIPPED_READS = "hide-softclipped-reads";

  constructor(props) {
    super(props);

    this.state = {
      selectedBAMFiles: [],
    };
  }

  componentDidMount() {
    const selectedBAMFiles: TrackNgsOrderedBamFilesData[] = this.props.initiallySelectedBAMFiles;
    const filenameFetchingPromises = selectedBAMFiles.map((bamFileData: TrackNgsOrderedBamFilesData) => {
      return this.props
        .getProjectFileById(bamFileData.bamFileId)
        .then((bamFileSummary: BamSummary) => {
          const bamFileSelection: FileSelection = {
            file: bamFileSummary.bamFile as BrowsingProjectFileFragment,
            color: bamFileData.coverageColor,
          };
          return bamFileSelection;
        })
        .catch((_e) => {
          return null; // can happen if file is not found or not accessible anymore
        });
    });
    Promise.all(filenameFetchingPromises).then((results) => {
      this.setState({ selectedBAMFiles: results.filter((bamFile) => bamFile != null) });
    });
  }

  handleApply = (values: FormData) => {
    const logLin: CoverageScaleLinLog = values[NgsTrackCustomizationDialog.FIELD_SCALE_LINLOG];
    const scaleType: CoverageScaleType = values[NgsTrackCustomizationDialog.FIELD_SCALE_TYPE];
    const scaleMin: number = values[NgsTrackCustomizationDialog.FIELD_SCALE_MIN];
    const scaleMax: number = values[NgsTrackCustomizationDialog.FIELD_SCALE_MAX];

    const configPayload: Partial<TrackNgsConfiguration> = {
      coverageConfig: {
        logScale: logLin === CoverageScaleLinLog.LOG,
        yAxisScaling: scaleType,
        yAxisScalingMin: scaleMin,
        yAxisScalingMax: scaleMax,
      },
      orderedBamFiles: this.state.selectedBAMFiles.map((selectedBAMFile) => {
        const initialConfig = this.props.initiallySelectedBAMFiles.find(
          (bamFileData) => bamFileData.bamFileId === selectedBAMFile.file.id,
        );
        const trackNgsOrderedBamFilesData: TrackNgsOrderedBamFilesData = {
          ...DEFAULT_NGS_TRACK_CONFIGURATION,
          bamFileId: selectedBAMFile.file.id,
          ...(initialConfig || {}), // keep intact the rest of the configuration (visibleCoverage, visiblePileup)
          coverageColor: selectedBAMFile.color,
        };
        return trackNgsOrderedBamFilesData;
      }),
    };

    try {
      this.props.applyNgsConfig(configPayload);
    } catch (e) {
      throw new SubmissionError({ _error: e.message });
    }
    this.props.onClose();
  };

  render(): ReactElement {
    return (
      <TabDialog
        headerProps={{ title: "NGS Track" }}
        mainActionButtonProps={{
          children: "Apply",
          onPress: this.props.handleSubmit(this.handleApply),
        }}
        footerMessage={{ message: this.props.error }}
        closeFn={this.props.onClose}
      >
        <TabItem key={"data"} title={"Data"}>
          <TrackFilesSelection
            selectedFiles={this.state.selectedBAMFiles}
            setSelectedFiles={(files: FileSelection[]) => this.setState({ selectedBAMFiles: files })}
            isSelectableFilter={(file: BrowsingProjectFileFragment) => {
              //@see FileNameCell.tsx
              const indexStatus = this.props.indexingStatusLookup[file.id] || file.indexingStatus;
              return (
                isBamFile(file) && (isIndexRequiredForGenomeViewer(file) ? indexStatus === IndexStatus.Exists : true)
              );
            }}
            baseFilterExtension={"BAM"}
            baseFilterHint={`Only indexed .bam files can be selected.`}
            defaultColor={DEFAULT_COVERAGE_HISTOGRAM_COLOR}
          />
        </TabItem>

        <TabItem key={"y-axis"} title={"Y-axis"}>
          <>Customize the Y-axis of your NGS Coverage tracks</>

          <RadioButtonReduxFormField
            name={NgsTrackCustomizationDialog.FIELD_SCALE_TYPE}
            component={RadioButtonSelector}
            horizontalLayout={false}
            radioButtonsAlignedVertically={true}
            options={coverageScaleTypeRadioOptions}
          />

          {this.props.selectedYAxisScaleType === CoverageScaleType.FIXED_COMMON && (
            <>
              <InputReduxFormField
                componentClass="input"
                name={NgsTrackCustomizationDialog.FIELD_SCALE_MIN}
                type={"number"}
                label="Minimum"
                placeholder="Min of the Y-axis"
                component={InputField}
                horizontalLayout={false}
                validate={simpleFieldValidation}
                normalize={(value: string): number => (!value?.length ? null : Number(value))}
              />

              <InputReduxFormField
                componentClass="input"
                name={NgsTrackCustomizationDialog.FIELD_SCALE_MAX}
                type={"number"}
                label="Maximum"
                placeholder="Max of the Y-axis"
                component={InputField}
                horizontalLayout={false}
                validate={simpleFieldValidation}
                normalize={(value: string): number => (!value?.length ? null : Number(value))}
              />
            </>
          )}

          <RadioButtonReduxFormField
            name={NgsTrackCustomizationDialog.FIELD_SCALE_LINLOG}
            component={RadioButtonSelector}
            horizontalLayout={false}
            radioButtonsAlignedVertically={true}
            options={coverageScaleLinLogRadioOptions}
          />
        </TabItem>
      </TabDialog>
    );
  }

  static validate = (values): { _error?: string } => {
    const selectedLinLog: string = values[NgsTrackCustomizationDialog.FIELD_SCALE_LINLOG];
    const selectedScaleType: string = values[NgsTrackCustomizationDialog.FIELD_SCALE_TYPE];
    const scaleMin: number = values[NgsTrackCustomizationDialog.FIELD_SCALE_MIN];
    const scaleMax: number = values[NgsTrackCustomizationDialog.FIELD_SCALE_MAX];

    if (
      selectedLinLog === CoverageScaleLinLog.LOG &&
      selectedScaleType === CoverageScaleType.FIXED_COMMON &&
      (scaleMin <= 0 || scaleMax <= 0)
    ) {
      return { _error: "With log scaling, both minimum and maximum values must be greater than 0." };
    }
    if (selectedScaleType === CoverageScaleType.FIXED_COMMON && scaleMin >= scaleMax) {
      return { _error: "The maximum value must be greater than the minimum value." };
    }
    return {};
  };
}

export default connect<
  INgsTrackCustomizationDialogAppStateProps,
  INgsTrackCustomizationDialogDispatchProps,
  INgsTrackCustomizationDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: NgsTrackCustomizationDialog.FORM_NAME,
    validate: NgsTrackCustomizationDialog.validate,
  })(NgsTrackCustomizationDialog as any),
);

export enum CoverageScaleType {
  DYNAMIC_COMMON = "dynamic_common",
  DYNAMIC_SEPARATE = "dynamic_separate",
  FIXED_COMMON = "fixed_common",
}

const coverageScaleTypeRadioOptions: IRadioProps[] = [
  {
    label: "Dynamic Common",
    name: CoverageScaleType.DYNAMIC_COMMON,
    tooltipText: "All tracks use the same zoom-dependent scaling.",
  },
  {
    label: "Dynamic Separate",
    name: CoverageScaleType.DYNAMIC_SEPARATE,
    tooltipText: "Automatically scales every track individually.",
  },
  {
    label: "Fixed Common",
    name: CoverageScaleType.FIXED_COMMON,
    tooltipText: "Set custom scaling applied to all tracks.",
  },
];

export enum CoverageScaleLinLog {
  LIN = "linear",
  LOG = "logarithmic",
}

const coverageScaleLinLogRadioOptions: IRadioProps[] = [
  { label: "linear", name: CoverageScaleLinLog.LIN, tooltipText: "Linear y-axis scaling." },
  { label: "logarithmic", name: CoverageScaleLinLog.LOG, tooltipText: "Logarithmic y-axis scaling." },
];

export const DEFAULT_NGS_COVERAGE_CONFIGURATION: TrackNgsCoverageConfiguration = {
  logScale: false,
  yAxisScaling: CoverageScaleType.DYNAMIC_SEPARATE,
  yAxisScalingMin: 0,
  yAxisScalingMax: 10,
};

export const DEFAULT_NGS_TRACK_CONFIGURATION: TrackNgsOrderedBamFilesData = {
  bamFileId: null,
  coverageColor: DEFAULT_COVERAGE_HISTOGRAM_COLOR,
  pileupDensity: NGS_READS_PILEUP_DENSITY.MEDIUM,
  pileupLanesCount: DEFAULT_NGS_LANES_COUNT,
  visiblePileup: false,
  pileupColoring: NGS_READS_PILEUP_COLORING.DEFAULT,
  pileupIncludeSoftClippedReads: false,
};
